import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'components/Dialog';
import Button from '@mui/material/Button';
import { UserId } from 'features/users/types';
import { kickUser } from 'features/users/actions';
import { PermissionTypes } from 'features/permissions/types';
import { useEffect, useId } from 'react';
import { usePermissions } from 'features/permissions/usePermissions';
import { selectActiveBreakoutUser } from 'features/breakout-rooms/breakoutRoomsSlice';

const KickUserConfirmation = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const userId = modalData.userId as UserId;
  const breakoutId = modalData.breakoutId as string | undefined;

  const user = useAppSelector((state) => selectActiveBreakoutUser(state, userId));

  const { hasPermissions } = usePermissions();

  const canRemoveParticipant = hasPermissions(PermissionTypes.removeParticipant, {
    targetRole: user?.role,
  });

  useEffect(() => {
    if (!user || !canRemoveParticipant) {
      dispatch(closeModal('kickUserConfirmation'));
    }
  }, [user, canRemoveParticipant, dispatch]);

  const handleOk = () => {
    dispatch(
      kickUser({
        userId,
        breakoutId,
      })
    );
    dispatch(closeModal('kickUserConfirmation'));
  };

  const handleCancel = () => {
    dispatch(closeModal('kickUserConfirmation'));
  };

  if (!user) {
    return null;
  }

  const userInBreakoutRoom = !!breakoutId;

  return (
    <Dialog
      open
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      aria-describedby={`${modalId}-description`}
    >
      <DialogTitle id={`${modalId}-title`}>
        {userInBreakoutRoom
          ? t('kick_user_confirm.title_breakout_user', { username: user.name })
          : t('kick_user_confirm.title', { username: user.name })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id={`${modalId}-description`}>
          {userInBreakoutRoom
            ? t('kick_user_confirm.description_breakout_user', { username: user.name })
            : t('kick_user_confirm.description', { username: user.name })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="grayscale">
          {t('kick_user_confirm.btn_cancel_label')}
        </Button>
        <Button onClick={handleOk}>{t('kick_user_confirm.btn_ok_label')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default KickUserConfirmation;
